import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/Urls";
import { toast } from "../hooks/use-toast";
import { useChatSocket } from "../contexts/ChatSocketContext.jsx";
import { CLOUDINARY_PURPOSES, uploadDirectToCloudinary } from "../lib/cloudinaryUpload";
import {
  ArrowLeft,
  Clock3,
  Copy,
  Crop,
  Image as ImageIcon,
  Mic,
  Music2,
  Pause,
  Pencil,
  Play,
  Reply,
  SendHorizontal,
  Trash2,
  Video,
  X,
} from "lucide-react";

const WhatsAppSingleTick = ({ className = "" }) => (
  <svg
    viewBox="0 0 18 14"
    aria-hidden="true"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.1 7.2 5.3 10.4 12.2 3.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WhatsAppDoubleTick = ({ className = "" }) => (
  <svg
    viewBox="0 0 21 14"
    aria-hidden="true"
    className={className}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.6 7.3 4.8 10.4 11.2 4"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.6 7.3 9.8 10.4 18.2 2.2"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

import ProfileImageViewer from "./ProfileImageViewer";
import {
  CHAT_DELIVERED_DESTINATION,
  CHAT_READ_DESTINATION,
  CHAT_TYPING_DESTINATION,
  chatDeliveryTopic,
  chatPresenceTopic,
  chatReadTopic,
  chatTypingTopic,
} from "../lib/chatRealtime";

const HISTORY_PAGE_SIZE = 50;
const HISTORY_CACHE_MAX = 100;
const MAX_VOICE_DURATION_MS = 5 * 60 * 1000;
const VOICE_MESSAGE_PREVIEW = "🎤 Voice message";

const IMAGE_MESSAGE_PREVIEW = "📷 Photo";
const VIDEO_MESSAGE_PREVIEW = "🎥 Video";
const AUDIO_MESSAGE_PREVIEW = "🎵 Audio";
const MAX_CHAT_IMAGE_BYTES = 15 * 1024 * 1024;
const MAX_CHAT_VIDEO_BYTES = 75 * 1024 * 1024;
const MAX_CHAT_AUDIO_BYTES = 50 * 1024 * 1024;

const MEDIA_CROP_PRESETS = [
  { id: "original", label: "Original", ratio: null },
  { id: "square", label: "1:1", ratio: 1, width: 1280, height: 1280 },
  { id: "portrait", label: "4:5", ratio: 4 / 5, width: 1080, height: 1350 },
  { id: "landscape", label: "16:9", ratio: 16 / 9, width: 1280, height: 720 },
];

const AUDIO_EXTENSIONS = new Set([
  "aac", "aif", "aiff", "amr", "au", "caf", "flac", "m4a", "mid",
  "midi", "mp3", "mp4", "oga", "ogg", "opus", "wav", "wave",
  "webm", "wma", "3gp", "3gpp",
]);

const getMessageType = (message) =>
  String(message?.messageType || "TEXT").toUpperCase();

const isImageMessage = (message) =>
  getMessageType(message) === "IMAGE" && Boolean(message?.attachmentUrl);

const isVideoMessage = (message) =>
  getMessageType(message) === "VIDEO" && Boolean(message?.attachmentUrl);

const isMediaAttachmentMessage = (message) =>
  isImageMessage(message) || isVideoMessage(message);

const isTextMessage = (message) =>
  getMessageType(message) === "TEXT";

const getMessagePreview = (message) => {
  if (isVoiceMessage(message)) {
    return message?.attachmentName
      ? `🎵 ${message.attachmentName}`
      : VOICE_MESSAGE_PREVIEW;
  }
  if (isImageMessage(message)) return IMAGE_MESSAGE_PREVIEW;
  if (isVideoMessage(message)) return VIDEO_MESSAGE_PREVIEW;
  return String(message?.message || "");
};

const formatBytes = (value = 0) => {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileExtension = (name = "") => {
  const match = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || "";
};

const normalizeAudioMimeType = (file) => {
  const declared = String(file?.type || "").toLowerCase();
  if (declared.startsWith("audio/") || declared === "application/ogg") {
    return declared;
  }

  const ext = getFileExtension(file?.name);
  const known = {
    mp3: "audio/mpeg", m4a: "audio/mp4", mp4: "audio/mp4", aac: "audio/aac",
    wav: "audio/wav", wave: "audio/wav", ogg: "audio/ogg", oga: "audio/ogg",
    opus: "audio/ogg", flac: "audio/flac", webm: "audio/webm", amr: "audio/amr",
    aif: "audio/aiff", aiff: "audio/aiff", au: "audio/basic", caf: "audio/x-caf",
    mid: "audio/midi", midi: "audio/midi", wma: "audio/x-ms-wma",
    "3gp": "audio/3gpp", "3gpp": "audio/3gpp",
  };
  return known[ext] || (ext ? `audio/x-${ext}` : "audio/octet-stream");
};

const getChatAttachmentDescriptor = (file, requestedType) => {
  const type = String(requestedType || "").toUpperCase();
  const mime = String(file?.type || "").toLowerCase();
  const extension = getFileExtension(file?.name);

  if (type === "IMAGE") {
    if (file.size > MAX_CHAT_IMAGE_BYTES) {
      throw new Error("Photos must be 15 MB or less.");
    }
    return { type, purpose: CLOUDINARY_PURPOSES.CHAT_IMAGE, preview: IMAGE_MESSAGE_PREVIEW };
  }

  if (type === "VIDEO") {
    if (file.size > MAX_CHAT_VIDEO_BYTES) {
      throw new Error("Videos must be 75 MB or less.");
    }
    return { type, purpose: CLOUDINARY_PURPOSES.CHAT_VIDEO, preview: VIDEO_MESSAGE_PREVIEW };
  }

  if (type === "AUDIO") {
    if (!mime.startsWith("audio/") && mime !== "application/ogg" && !AUDIO_EXTENSIONS.has(extension)) {
      throw new Error("Choose an audio file.");
    }
    if (file.size > MAX_CHAT_AUDIO_BYTES) {
      throw new Error("Audio files must be 50 MB or less.");
    }
    return { type, purpose: CLOUDINARY_PURPOSES.CHAT_AUDIO, preview: `🎵 ${file.name || "Audio"}` };
  }

  throw new Error("Only photos, videos and audio can be attached.");
};

const cropGravityFromPosition = (x = 50, y = 50) => {
  const horizontal = x < 34 ? "west" : x > 66 ? "east" : "";
  const vertical = y < 34 ? "north" : y > 66 ? "south" : "";
  return [vertical, horizontal].filter(Boolean).join("_") || "center";
};

const buildCroppedCloudinaryUrl = (url, crop) => {
  if (!url || !crop || crop.presetId === "original") return url;

  const preset = MEDIA_CROP_PRESETS.find((item) => item.id === crop.presetId);
  if (!preset?.width || !preset?.height) return url;

  const marker = "/upload/";
  const markerIndex = url.indexOf(marker);
  if (markerIndex < 0) return url;

  const gravity = cropGravityFromPosition(crop.positionX, crop.positionY);
  const transformation = `c_fill,g_${gravity},w_${preset.width},h_${preset.height},q_auto,f_auto`;

  return `${url.slice(0, markerIndex + marker.length)}${transformation}/${url.slice(
    markerIndex + marker.length
  )}`;
};

const buildCloudinaryAudioPlaybackUrl = (url) => {
  if (!url) return url;
  // Cloudinary audio uses the video asset type. Deliver attached audio as MP3
  // so the chat player has a broadly playable output even when the uploaded
  // source format itself is not natively decoded by the browser.
  return url.replace(/\.[^./?]+(?=\?|$)/, ".mp3");
};

const getAudioDurationMs = (file) =>
  new Promise((resolve, reject) => {
    const localUrl = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.onloadedmetadata = () => {
      const seconds = Number(audio.duration);
      URL.revokeObjectURL(localUrl);
      if (!Number.isFinite(seconds) || seconds <= 0) {
        resolve(null);
        return;
      }
      resolve(Math.max(1, Math.round(seconds * 1000)));
    };
    audio.onerror = () => {
      URL.revokeObjectURL(localUrl);
      resolve(null);
    };
    audio.src = localUrl;
  });

const isVoiceMessage = (message) =>
  String(message?.messageType || "").toUpperCase() === "AUDIO" &&
  Boolean(message?.audioUrl);

const formatVoiceDuration = (milliseconds = 0) => {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const getPreferredRecorderMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";

  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];

  return candidates.find((type) => MediaRecorder.isTypeSupported?.(type)) || "";
};

const getAudioFileExtension = (mimeType = "") => {
  const type = String(mimeType).toLowerCase();
  if (type.includes("ogg")) return "ogg";
  if (type.includes("mp4")) return "m4a";
  if (type.includes("mpeg")) return "mp3";
  if (type.includes("wav")) return "wav";
  return "webm";
};

const getTimestamp = (message) => {
  const raw =
    message?.timestamp ??
    message?.createdAt ??
    message?.sentAt ??
    message?.date;

  if (raw === null || raw === undefined || raw === "") return 0;

  if (typeof raw === "number") {
    return raw < 1_000_000_000_000 ? raw * 1000 : raw;
  }

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
  }

  const parsed = new Date(raw).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getServerMessageId = (message) =>
  message?.messageId ?? message?.id ?? null;

const getMessageIdentity = (message) => {
  const serverId = getServerMessageId(message);
  if (serverId !== null && serverId !== undefined) {
    return `id:${serverId}`;
  }

  const clientMessageId = message?.clientMessageId;
  if (clientMessageId) {
    return `client:${clientMessageId}`;
  }

  return [
    "fallback",
    message?.roomId ?? "",
    message?.senderId ?? "",
    message?.receiverId ?? "",
    message?.itemId ?? "",
    getTimestamp(message),
    message?.messageType ?? "",
    message?.audioUrl ?? "",
    message?.attachmentUrl ?? "",
    message?.message ?? "",
  ].join(":");
};

const sameMessage = (existing, incoming) => {
  const existingServerId = getServerMessageId(existing);
  const incomingServerId = getServerMessageId(incoming);

  if (
    existingServerId !== null &&
    incomingServerId !== null &&
    String(existingServerId) === String(incomingServerId)
  ) {
    return true;
  }

  const existingClientId = existing?.clientMessageId;
  const incomingClientId = incoming?.clientMessageId;

  return Boolean(
    existingClientId &&
      incomingClientId &&
      existingClientId === incomingClientId
  );
};

const mergeMessages = (current, incoming) => {
  const merged = [...current];

  incoming.forEach((nextMessage) => {
    if (!nextMessage) return;

    const existingIndex = merged.findIndex((existing) =>
      sameMessage(existing, nextMessage)
    );

    if (existingIndex >= 0) {
      const combined = {
        ...merged[existingIndex],
        ...nextMessage,
      };

      // A server id means the optimistic send has been persisted.
      if (getServerMessageId(nextMessage) !== null) {
        combined._optimistic = false;
        combined._failed = false;
      }

      merged[existingIndex] = combined;
    } else {
      merged.push(nextMessage);
    }
  });

  return merged.sort((a, b) => {
    const aTime = getTimestamp(a);
    const bTime = getTimestamp(b);

    if (!aTime && !bTime) return 0;
    if (!aTime) return 1;
    if (!bTime) return -1;
    return aTime - bTime;
  });
};

const formatMessageTime = (message) => {
  const timestamp = getTimestamp(message);
  if (!timestamp) return "";

  return new Date(timestamp).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const readHistoryCache = (key) => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || "[]");
    return Array.isArray(cached) ? cached : [];
  } catch {
    sessionStorage.removeItem(key);
    return [];
  }
};

const resizeGrowingTextarea = (element, maxHeight = 160) => {
  if (!element) return;

  element.style.height = "auto";
  const nextHeight = Math.min(element.scrollHeight, maxHeight);
  element.style.height = `${nextHeight}px`;
  element.style.overflowY =
    element.scrollHeight > maxHeight ? "auto" : "hidden";
};

const VoiceNotePlayer = ({
  src,
  durationMs = 0,
  isMe = false,
}) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(
    Math.max(0, Number(durationMs || 0) / 1000)
  );

  useEffect(() => {
    setCurrentTime(0);
    setPlaying(false);
    setDuration(Math.max(0, Number(durationMs || 0) / 1000));
  }, [durationMs, src]);

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (audio.paused) {
        await audio.play();
      } else {
        audio.pause();
      }
    } catch (error) {
      console.error("Unable to play voice note", error);
    }
  };

  const seek = (event) => {
    const audio = audioRef.current;
    if (!audio) return;

    const next = Number(event.target.value);
    audio.currentTime = next;
    setCurrentTime(next);
  };

  return (
    <div
      data-no-swipe="true"
      className="flex min-w-[210px] max-w-[280px] items-center gap-2"
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        onLoadedMetadata={(event) => {
          const loaded = Number(event.currentTarget.duration);
          if (Number.isFinite(loaded) && loaded > 0) {
            setDuration(loaded);
          }
        }}
      />

      <button
        type="button"
        onClick={togglePlayback}
        aria-label={playing ? "Pause voice note" : "Play voice note"}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
          isMe
            ? "bg-white/15 text-white hover:bg-white/20"
            : "bg-primary/10 text-primary hover:bg-primary/15"
        }`}
      >
        {playing ? (
          <Pause className="h-4 w-4" fill="currentColor" />
        ) : (
          <Play className="ml-0.5 h-4 w-4" fill="currentColor" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <input
          type="range"
          min="0"
          max={Math.max(duration, 0.01)}
          step="0.05"
          value={Math.min(currentTime, Math.max(duration, 0.01))}
          onChange={seek}
          aria-label="Voice note progress"
          className={`h-1 w-full cursor-pointer accent-current ${
            isMe ? "text-white" : "text-primary"
          }`}
        />

        <div
          className={`mt-1 flex items-center justify-between text-[10px] ${
            isMe ? "text-white/65" : "text-muted-foreground"
          }`}
        >
          <span>
            {formatVoiceDuration(
              currentTime > 0 ? currentTime * 1000 : durationMs
            )}
          </span>
          <Mic className="h-3.5 w-3.5" />
        </div>
      </div>
    </div>
  );
};

const AttachmentContent = ({ message, onOpen }) => {
  const url = message?.attachmentUrl;
  const name = message?.attachmentName || (isImageMessage(message) ? "Photo" : "Video");
  const progress = Number(message?._uploadProgress || 0);
  const uploading = Boolean(message?._uploading);

  if (isImageMessage(message)) {
    return (
      <button
        type="button"
        data-no-swipe="true"
        onClick={() => !uploading && onOpen?.(message)}
        className="relative block max-w-[280px] overflow-hidden rounded-md text-left"
      >
        <img
          src={url}
          alt={name}
          loading="lazy"
          className="max-h-[340px] w-full min-w-[180px] object-cover"
        />
        {uploading && (
          <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 text-[10px] text-white">
            Uploading {progress}%
          </div>
        )}
      </button>
    );
  }

  if (isVideoMessage(message)) {
    return (
      <button
        type="button"
        data-no-swipe="true"
        onClick={() => !uploading && onOpen?.(message)}
        className="group relative block max-w-[300px] overflow-hidden rounded-md bg-black text-left"
      >
        <video
          src={url}
          muted
          playsInline
          preload="metadata"
          className="pointer-events-none max-h-[340px] w-full min-w-[210px] object-cover"
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/10 transition group-hover:bg-black/20">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white shadow-lg">
            <Play className="ml-0.5 h-5 w-5" fill="currentColor" />
          </span>
        </span>
        {uploading && (
          <div className="absolute inset-x-0 bottom-0 bg-black/65 px-2 py-1 text-[10px] text-white">
            Uploading {progress}%
          </div>
        )}
      </button>
    );
  }

  return null;
};

const MediaViewer = ({ media, onClose }) => {
  useEffect(() => {
    if (!media) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [media, onClose]);

  if (!media) return null;

  const isVideo = isVideoMessage(media);
  const url = media.attachmentUrl;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-3 sm:p-6"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close media"
        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
      >
        <X className="h-5 w-5" />
      </button>

      {isVideo ? (
        <video
          src={url}
          controls
          autoPlay
          playsInline
          className="max-h-[90vh] max-w-[96vw] rounded-lg bg-black object-contain shadow-2xl"
        />
      ) : (
        <img
          src={url}
          alt={media.attachmentName || "Chat photo"}
          className="max-h-[90vh] max-w-[96vw] select-none rounded-lg object-contain shadow-2xl"
        />
      )}
    </div>
  );
};

const MediaSendPreview = ({ draft, onCancel, onSend, sending = false }) => {
  const [presetId, setPresetId] = useState("original");
  const [positionX, setPositionX] = useState(50);
  const [positionY, setPositionY] = useState(50);

  useEffect(() => {
    setPresetId("original");
    setPositionX(50);
    setPositionY(50);
  }, [draft?.objectUrl]);

  if (!draft) return null;

  const preset = MEDIA_CROP_PRESETS.find((item) => item.id === presetId) || MEDIA_CROP_PRESETS[0];
  const cropped = Boolean(preset.ratio);
  const previewStyle = cropped
    ? {
        aspectRatio: String(preset.ratio),
        objectFit: "cover",
        objectPosition: `${positionX}% ${positionY}%`,
      }
    : { maxHeight: "58vh", objectFit: "contain" };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-3 sm:p-5">
      <div className="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">Preview {draft.type === "IMAGE" ? "photo" : "video"}</p>
            <p className="truncate text-xs text-muted-foreground">{draft.file.name}</p>
          </div>
          <button type="button" onClick={onCancel} className="rounded-full p-2 hover:bg-muted" aria-label="Cancel attachment">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="chat-tiny-scrollbar flex-1 overflow-y-auto p-4">
          <div className="mx-auto flex max-w-2xl items-center justify-center overflow-hidden rounded-xl bg-black">
            {draft.type === "IMAGE" ? (
              <img src={draft.objectUrl} alt="Preview" className="w-full" style={previewStyle} />
            ) : (
              <video src={draft.objectUrl} controls playsInline className="w-full" style={previewStyle} />
            )}
          </div>

          <div className="mt-4 rounded-xl border border-border bg-secondary/35 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
              <Crop className="h-4 w-4 text-primary" />
              Crop
            </div>

            <div className="flex flex-wrap gap-2">
              {MEDIA_CROP_PRESETS.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setPresetId(item.id)}
                  className={`rounded-full px-3 py-1.5 text-xs transition ${
                    presetId === item.id ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {cropped && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="text-[11px] text-muted-foreground">
                  Horizontal focus
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={positionX}
                    onChange={(event) => setPositionX(Number(event.target.value))}
                    className="mt-1 w-full accent-current text-primary"
                  />
                </label>
                <label className="text-[11px] text-muted-foreground">
                  Vertical focus
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={positionY}
                    onChange={(event) => setPositionY(Number(event.target.value))}
                    className="mt-1 w-full accent-current text-primary"
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border p-3">
          <button type="button" onClick={onCancel} disabled={sending} className="rounded-full px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={sending}
            onClick={() => onSend?.({ presetId, positionX, positionY })}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <SendHorizontal className="h-4 w-4" />
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AudioSendPreview = ({ draft, onCancel, onSend, sending = false }) => {
  if (!draft) return null;

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-4 shadow-2xl">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold">Audio attachment</p>
            <p className="truncate text-xs text-muted-foreground">{draft.file.name}</p>
          </div>
          <button type="button" onClick={onCancel} className="rounded-full p-2 hover:bg-muted" aria-label="Cancel audio attachment">
            <X className="h-5 w-5" />
          </button>
        </div>

        <audio src={draft.objectUrl} controls preload="metadata" className="mt-4 w-full" />
        <p className="mt-2 text-[11px] text-muted-foreground">{formatBytes(draft.file.size)}</p>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={sending} className="rounded-full px-4 py-2 text-sm hover:bg-muted disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <SendHorizontal className="h-4 w-4" />
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SWIPE_REPLY_THRESHOLD = 54;
const SWIPE_REPLY_MAX = 78;
const LONG_PRESS_MS = 480;

const SwipeableMessage = ({
  children,
  onReply,
  onLongPress,
  replyDisabled = false,
  actionsDisabled = false,
}) => {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gestureRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearLongPressTimer();
    gestureRef.current = null;
    longPressTriggeredRef.current = false;
    setDragging(false);
    setOffset(0);
  }, [clearLongPressTimer]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  const handlePointerDown = useCallback(
    (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (
        event.target.closest(
          "button, textarea, input, a, select, [data-no-swipe='true']"
        )
      ) {
        return;
      }

      const canSwipe = !replyDisabled;

      gestureRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        horizontal: false,
        offset: 0,
        canSwipe,
      };

      longPressTriggeredRef.current = false;

      if (!actionsDisabled && onLongPress) {
        longPressTimerRef.current = setTimeout(() => {
          longPressTimerRef.current = null;
          longPressTriggeredRef.current = true;
          gestureRef.current = null;
          setDragging(false);
          setOffset(0);
          onLongPress();
        }, LONG_PRESS_MS);
      }

      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [actionsDisabled, onLongPress, replyDisabled]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;

      if (Math.abs(dx) >= 8 || Math.abs(dy) >= 8) {
        clearLongPressTimer();
      }

      if (!gesture.canSwipe) {
        return;
      }

      if (!gesture.horizontal) {
        if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          gestureRef.current = null;
          setOffset(0);
          return;
        }
        gesture.horizontal = true;
        setDragging(true);
      }

      if (dx <= 0) {
        gesture.offset = 0;
        setOffset(0);
        return;
      }

      event.preventDefault();
      const nextOffset = Math.min(dx, SWIPE_REPLY_MAX);
      gesture.offset = nextOffset;
      setOffset(nextOffset);
    },
    [clearLongPressTimer]
  );

  const finishSwipe = useCallback(
    (event) => {
      clearLongPressTimer();

      if (longPressTriggeredRef.current) {
        reset();
        return;
      }

      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== event.pointerId) {
        reset();
        return;
      }

      const shouldReply =
        gesture.canSwipe &&
        gesture.offset >= SWIPE_REPLY_THRESHOLD;

      reset();

      if (shouldReply) {
        onReply?.();
      }
    },
    [clearLongPressTimer, onReply, reset]
  );

  const handleContextMenu = useCallback(
    (event) => {
      if (actionsDisabled || !onLongPress) return;
      if (
        event.target.closest(
          "button, textarea, input, a, select, [data-no-swipe='true']"
        )
      ) {
        return;
      }

      event.preventDefault();
      clearLongPressTimer();
      gestureRef.current = null;
      setDragging(false);
      setOffset(0);
      onLongPress();
    },
    [actionsDisabled, clearLongPressTimer, onLongPress]
  );

  return (
    <div
      className="relative w-fit max-w-[85%] sm:max-w-[72%] touch-pan-y select-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishSwipe}
      onPointerCancel={reset}
      onContextMenu={handleContextMenu}
    >
      {!replyDisabled && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-10 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-primary/15 text-primary"
          style={{
            opacity: Math.min(offset / SWIPE_REPLY_THRESHOLD, 1),
            transform: `translateY(-50%) scale(${0.75 +
              Math.min(offset / SWIPE_REPLY_THRESHOLD, 1) * 0.25})`,
          }}
        >
          <Reply className="h-4 w-4" />
        </div>
      )}

      <div
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? "none" : "transform 160ms ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const ChatWindow = ({
  userId,
  otherUserId,
  itemId,
  otherUser = null,
  onRoomRead,
  onBack = null,
}) => {
  const participantId = Number(otherUserId);
  const numericUserId = Number(userId);
  const numericItemId = Number(itemId);

  const {
    connectionState,
    connected,
    subscribe,
    publish,
  } = useChatSocket();

  const roomId = useMemo(
    () =>
      `${numericItemId}_${Math.min(numericUserId, participantId)}_${Math.max(
        numericUserId,
        participantId
      )}`,
    [numericItemId, numericUserId, participantId]
  );

  const draftKey = `chat_draft_${numericUserId}_${roomId}`;
  const historyCacheKey = `chat_history_${numericUserId}_${roomId}`;

  const [messages, setMessages] = useState(() => readHistoryCache(historyCacheKey));
  const [text, setText] = useState(() => sessionStorage.getItem(draftKey) || "");
  const [itemName, setItemName] = useState("");
  const [itemAvailability, setItemAvailability] = useState("checking");
  const [participant, setParticipant] = useState(otherUser);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoadingEarlier, setHistoryLoadingEarlier] = useState(false);
  const [participantTyping, setParticipantTyping] = useState(false);
  const [participantPresence, setParticipantPresence] = useState(null);
  const [readReceipt, setReadReceipt] = useState(null);
  const [deliveryReceipt, setDeliveryReceipt] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [messageActions, setMessageActions] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [attachmentDraft, setAttachmentDraft] = useState(null);
  const [attachmentSending, setAttachmentSending] = useState(false);
  const [mediaViewerMessage, setMediaViewerMessage] = useState(null);

  const bottomRef = useRef(null);
  const messageInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const messageNodeRefs = useRef(new Map());
  const replyHighlightTimeoutRef = useRef(null);
  const missingItemAlertRoomRef = useRef(null);
  const onRoomReadRef = useRef(onRoomRead);
  const typingTimeoutRef = useRef(null);
  const typingSentRef = useRef(false);
  const lastReadMessageIdRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartedAtRef = useRef(0);
  const recordingActionRef = useRef("cancel");

  useEffect(() => {
    onRoomReadRef.current = onRoomRead;
  }, [onRoomRead]);

  const markConversationRead = useCallback(
    (message = null) => {
      const activityTime = getTimestamp(message) || Date.now();
      const messageId = message?.messageId ?? message?.id ?? null;
      const numericMessageId = Number(messageId);
      const previousReadId = Number(lastReadMessageIdRef.current || 0);

      if (
        Number.isFinite(numericMessageId) &&
        numericMessageId > 0 &&
        numericMessageId <= previousReadId
      ) {
        return;
      }

      if (Number.isFinite(numericMessageId) && numericMessageId > 0) {
        lastReadMessageIdRef.current = numericMessageId;
      }

      onRoomReadRef.current?.(activityTime);

      const publishResult = publish(
        CHAT_READ_DESTINATION,
        {
          roomId,
          lastMessageId: messageId,
          readAt: Date.now(),
        },
        {
          queueIfDisconnected: false,
        }
      );

      if (publishResult === "published") {
        return;
      }

      api
        .put(`/api/chat/rooms/${encodeURIComponent(roomId)}/read`, {
          lastMessageId: messageId,
          readAt: Date.now(),
        })
        .catch((error) => {
          console.error("Failed to persist chat read state", error);
        });
    },
    [publish, roomId]
  );

  // Reset room-specific UI immediately when the conversation changes.
  useEffect(() => {
    setMessages(readHistoryCache(historyCacheKey));
    setHistoryLoading(true);
    setHistoryError("");
    setHistoryHasMore(false);
    setHistoryLoadingEarlier(false);
    setItemName("");
    setItemAvailability("checking");
    setText(sessionStorage.getItem(draftKey) || "");
    setParticipantTyping(false);
    setParticipantPresence(null);
    setReadReceipt(null);
    setDeliveryReceipt(null);
    setEditingMessageId(null);
    setEditText("");
    setEditSaving(false);
    setEditError("");
    setReplyingTo(null);
    setHighlightedMessageId(null);
    setMessageActions(null);
    setIsRecording(false);
    setRecordingElapsedMs(0);
    messageNodeRefs.current.clear();
    lastReadMessageIdRef.current = null;
  }, [roomId, draftKey, historyCacheKey]);

  // Keep drafts isolated per conversation.
  useEffect(() => {
    if (!text) {
      sessionStorage.removeItem(draftKey);
      return;
    }

    sessionStorage.setItem(draftKey, text);
  }, [draftKey, text]);

  // Keep a small room-scoped cache so reopening a conversation is instant.
  useEffect(() => {
    if (!messages.length) {
      return;
    }

    const recent = messages.slice(-HISTORY_CACHE_MAX);
    sessionStorage.setItem(historyCacheKey, JSON.stringify(recent));
  }, [historyCacheKey, messages]);

  // The normal composer is reused for new messages, replies and edits.
  useEffect(() => {
    resizeGrowingTextarea(messageInputRef.current);
  }, [text, editText, editingMessageId]);

  // When Edit is selected, focus the normal composer and place the caret at the end.
  useEffect(() => {
    if (editingMessageId === null) return;

    const frame = requestAnimationFrame(() => {
      const input = messageInputRef.current;
      if (!input) return;

      input.focus();
      resizeGrowingTextarea(input);

      const caret = input.value.length;
      input.setSelectionRange?.(caret, caret);
    });

    return () => cancelAnimationFrame(frame);
  }, [editingMessageId]);

  useEffect(() => {
    return () => {
      if (replyHighlightTimeoutRef.current) {
        clearTimeout(replyHighlightTimeoutRef.current);
      }
    };
  }, []);

  // ================= ITEM =================
  useEffect(() => {
    let cancelled = false;
    const cacheKey = `item_${numericItemId}`;
    const cached = sessionStorage.getItem(cacheKey);

    // Cached metadata may render immediately, but never skip the server check.
    // A listing may have been deleted since this room was last opened.
    if (cached) {
      setItemName(cached);
    } else {
      setItemName(`Item #${numericItemId}`);
    }

    setItemAvailability("checking");

    api
      .get(`/api/category-items/${numericItemId}`)
      .then((res) => {
        if (cancelled) return;

        const name = res.data?.itemName; {/*|| cached || `Item #${numericItemId}`*/}
        setItemName(name);
        setItemAvailability("available");
        sessionStorage.setItem(cacheKey, name);
        missingItemAlertRoomRef.current = null;
      })
      .catch((error) => {
        if (cancelled) return;

        const status = error?.response?.status;

        if (status === 404 || status === 410) {
          setItemAvailability("missing");
          setReplyingTo(null);

          if (missingItemAlertRoomRef.current !== roomId) {
            missingItemAlertRoomRef.current = roomId;
            toast({
              title: "Item no longer exists",
              description:
                "This conversation is still available, but it is now read-only.",
              duration: 5000,
            });
          }

          return;
        }

        // A temporary network problem should not falsely label the item deleted.
        console.error("Failed to verify chat item", error);
        setItemAvailability("unknown");
      });

    return () => {
      cancelled = true;
    };
  }, [numericItemId, roomId]);

  // ================= PARTICIPANT =================
  useEffect(() => {
    if (Number(otherUser?.userId) === participantId) {
      setParticipant(otherUser);
      return undefined;
    }

    let cancelled = false;

    api
      .get(`/api/users/${participantId}`)
      .then((res) => {
        if (!cancelled) {
          setParticipant(res.data || null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load chat participant", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [participantId, otherUser]);

  // ================= PRESENCE =================
  useEffect(() => {
    if (!participantId) {
      return undefined;
    }

    let cancelled = false;

    api
      .get(`/api/chat/presence/${participantId}`)
      .then((res) => {
        if (!cancelled) {
          setParticipantPresence(res.data || null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load chat presence", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [participantId]);

  // ================= HISTORY =================
  useEffect(() => {
    let cancelled = false;

    setHistoryLoading(true);
    setHistoryError("");

    api
      .get("/api/chat/history", {
        params: {
          roomId,
          limit: HISTORY_PAGE_SIZE,
        },
      })
      .then((res) => {
        if (cancelled) return;

        const history = Array.isArray(res.data) ? res.data : [];
        setMessages((current) => mergeMessages(current, history));
        setHistoryHasMore(history.length === HISTORY_PAGE_SIZE);

        const latestIncoming = [...history]
          .filter((message) => Number(message?.senderId) === participantId)
          .sort((a, b) => getTimestamp(b) - getTimestamp(a))[0];

        if (latestIncoming) {
          markConversationRead(latestIncoming);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load chat history", error);
        setHistoryError("Unable to load recent messages.");
      })
      .finally(() => {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [markConversationRead, participantId, roomId]);

  const loadEarlierMessages = useCallback(async () => {
    if (historyLoadingEarlier || !historyHasMore || !messages.length) return;

    const oldestServerMessage = messages.find(
      (message) => message?.messageId !== null && message?.messageId !== undefined
    );
    const beforeMessageId = oldestServerMessage?.messageId ?? oldestServerMessage?.id;

    if (!beforeMessageId) {
      setHistoryHasMore(false);
      return;
    }

    try {
      setHistoryLoadingEarlier(true);
      const res = await api.get("/api/chat/history", {
        params: {
          roomId,
          beforeMessageId,
          limit: HISTORY_PAGE_SIZE,
        },
      });

      const earlier = Array.isArray(res.data) ? res.data : [];
      setMessages((current) => mergeMessages(earlier, current));
      setHistoryHasMore(earlier.length === HISTORY_PAGE_SIZE);
    } catch (error) {
      console.error("Failed to load earlier messages", error);
    } finally {
      setHistoryLoadingEarlier(false);
    }
  }, [historyHasMore, historyLoadingEarlier, messages, roomId]);

  // ================= SHARED APP SOCKET =================
  useEffect(() => {
    if (!roomId) return undefined;

    const unsubscribeRoom = subscribe(
      `/topic/chat/${roomId}`,
      (frame) => {
        try {
          const incoming = JSON.parse(frame.body);
          setMessages((current) => mergeMessages(current, [incoming]));

          if (Number(incoming?.senderId) === participantId) {
            const messageId = incoming?.messageId ?? incoming?.id ?? null;

            if (messageId !== null) {
              publish(
                CHAT_DELIVERED_DESTINATION,
                {
                  roomId,
                  messageId,
                  deliveredAt: Date.now(),
                },
                {
                  queueIfDisconnected: false,
                }
              );
            }

            markConversationRead(incoming);
          }
        } catch (error) {
          console.error("Invalid chat message payload", error);
        }
      }
    );

    const unsubscribeTyping = subscribe(
      chatTypingTopic(roomId),
      (frame) => {
        try {
          const update = JSON.parse(frame.body);
          if (Number(update?.userId ?? update?.senderId) === participantId) {
            setParticipantTyping(Boolean(update?.typing));
          }
        } catch (error) {
          console.error("Invalid typing payload", error);
        }
      }
    );

    const unsubscribeRead = subscribe(
      chatReadTopic(roomId),
      (frame) => {
        try {
          const update = JSON.parse(frame.body);
          if (Number(update?.readerId ?? update?.userId) === participantId) {
            setReadReceipt(update);
          }
        } catch (error) {
          console.error("Invalid read receipt payload", error);
        }
      }
    );

    const unsubscribeDelivery = subscribe(
      chatDeliveryTopic(roomId),
      (frame) => {
        try {
          const update = JSON.parse(frame.body);
          if (Number(update?.recipientId ?? update?.userId) === participantId) {
            setDeliveryReceipt(update);
          }
        } catch (error) {
          console.error("Invalid delivery receipt payload", error);
        }
      }
    );

    const unsubscribePresence = subscribe(
      chatPresenceTopic(participantId),
      (frame) => {
        try {
          setParticipantPresence(JSON.parse(frame.body));
        } catch (error) {
          console.error("Invalid presence payload", error);
        }
      }
    );

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      if (typingSentRef.current) {
        publish(
          CHAT_TYPING_DESTINATION,
          {
            roomId,
            receiverId: participantId,
            typing: false,
          },
          {
            queueIfDisconnected: false,
          }
        );
      }

      typingSentRef.current = false;

      unsubscribePresence();
      unsubscribeDelivery();
      unsubscribeRead();
      unsubscribeTyping();
      unsubscribeRoom();
    };
  }, [
    markConversationRead,
    participantId,
    publish,
    roomId,
    subscribe,
  ]);

  // ================= AUTO SCROLL =================
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [messages]);

  const publishTyping = useCallback(
    (typing) => {
      publish(
        CHAT_TYPING_DESTINATION,
        {
          roomId,
          receiverId: participantId,
          typing,
        },
        {
          queueIfDisconnected: false,
        }
      );
    },
    [participantId, publish, roomId]
  );

  const itemMissing = itemAvailability === "missing";

  const handleTextChange = useCallback(
    (value) => {
      if (itemMissing) return;

      setText(value);

      const hasText = Boolean(value.trim());

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }

      if (!hasText) {
        if (typingSentRef.current) {
          publishTyping(false);
          typingSentRef.current = false;
        }
        return;
      }

      if (!typingSentRef.current) {
        publishTyping(true);
        typingSentRef.current = true;
      }

      typingTimeoutRef.current = setTimeout(() => {
        publishTyping(false);
        typingSentRef.current = false;
        typingTimeoutRef.current = null;
      }, 1200);
    },
    [itemMissing, publishTyping]
  );

  const startReplyingToMessage = useCallback(
    (message) => {
      const messageId = getServerMessageId(message);

      if (
        itemMissing ||
        !messageId ||
        message?._optimistic ||
        message?._failed
      ) {
        return;
      }

      setReplyingTo({
        messageId,
        senderId: Number(message?.senderId),
        message: getMessagePreview(message),
      });

      requestAnimationFrame(() => {
        messageInputRef.current?.focus();
      });
    },
    [itemMissing]
  );

  const cancelReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  const closeMessageActions = useCallback(() => {
    setMessageActions(null);
  }, []);

  const openMessageActions = useCallback((message) => {
    if (!message) return;

    setMessageActions({
      message,
      messageId: getServerMessageId(message),
    });
  }, []);

  const copyMessageText = useCallback(
    async (message) => {
      const value = String(message?.message || "");
      if (!value) {
        closeMessageActions();
        return;
      }

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = value;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        toast({
          title: "Message copied",
          duration: 1600,
        });
      } catch (error) {
        console.error("Failed to copy message", error);
        toast({
          title: "Unable to copy message",
          duration: 2000,
        });
      } finally {
        closeMessageActions();
      }
    },
    [closeMessageActions]
  );

  const scrollToRepliedMessage = useCallback((messageId) => {
    if (!messageId) return;

    const node = messageNodeRefs.current.get(String(messageId));

    if (!node) {
      toast({
        title: "Original message is earlier",
        description: "Load earlier messages to jump to this reply.",
        duration: 2500,
      });
      return;
    }

    node.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setHighlightedMessageId(String(messageId));

    if (replyHighlightTimeoutRef.current) {
      clearTimeout(replyHighlightTimeoutRef.current);
    }

    replyHighlightTimeoutRef.current = setTimeout(() => {
      setHighlightedMessageId(null);
      replyHighlightTimeoutRef.current = null;
    }, 1400);
  }, []);

  useEffect(() => {
    if (!messageActions) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMessageActions(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [messageActions]);

  // ================= VOICE NOTES =================
  const clearRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const stopMicrophoneTracks = useCallback(() => {
    mediaStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    mediaStreamRef.current = null;
  }, []);

  const uploadVoiceNote = useCallback(
    async (blob, durationMs, mimeType) => {
      if (!blob?.size || itemMissing) return;

      const clientMessageId =
        globalThis.crypto?.randomUUID?.() ||
        `${numericUserId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const localAudioUrl = URL.createObjectURL(blob);
      const replySnapshot = replyingTo;

      const optimisticMessage = {
        clientMessageId,
        senderId: numericUserId,
        receiverId: participantId,
        itemId: numericItemId,
        message: VOICE_MESSAGE_PREVIEW,
        messageType: "AUDIO",
        audioUrl: localAudioUrl,
        audioDurationMs: durationMs,
        audioMimeType: mimeType,
        roomId,
        timestamp: Date.now(),
        replyToMessageId: replySnapshot?.messageId ?? null,
        replyToSenderId: replySnapshot?.senderId ?? null,
        replyToMessage: replySnapshot?.message ?? null,
        _optimistic: true,
        _uploading: true,
        _uploadProgress: 0,
      };

      setMessages((current) => mergeMessages(current, [optimisticMessage]));
      setReplyingTo(null);

      try {
        const extension = getAudioFileExtension(mimeType);
        const voiceFile = new File(
          [blob],
          `voice-${Date.now()}.${extension}`,
          { type: mimeType || "audio/webm" }
        );

        const uploaded = await uploadDirectToCloudinary(
          voiceFile,
          CLOUDINARY_PURPOSES.CHAT_AUDIO,
          {
            onProgress: (progress) => {
              setMessages((current) =>
                current.map((message) =>
                  message.clientMessageId === clientMessageId
                    ? { ...message, _uploadProgress: progress }
                    : message
                )
              );
            },
          }
        );

        setMessages((current) =>
          current.map((message) =>
            message.clientMessageId === clientMessageId
              ? {
                  ...message,
                  audioUrl: uploaded.secureUrl,
                  attachmentPublicId: uploaded.publicId,
                  attachmentSizeBytes: uploaded.bytes || voiceFile.size,
                  _uploading: false,
                  _uploadProgress: 100,
                }
              : message
          )
        );

        URL.revokeObjectURL(localAudioUrl);

        const result = publish(
          "/app/chat.send",
          {
            clientMessageId,
            receiverId: participantId,
            itemId: numericItemId,
            roomId,
            messageType: "AUDIO",
            message: VOICE_MESSAGE_PREVIEW,
            audioUrl: uploaded.secureUrl,
            audioDurationMs: Math.max(1, Math.round(durationMs)),
            audioMimeType: mimeType,
            attachmentPublicId: uploaded.publicId,
            attachmentSizeBytes: uploaded.bytes || voiceFile.size,
            replyToMessageId: replySnapshot?.messageId ?? null,
          },
          {
            queueIfDisconnected: true,
            queueKey: `chat-send:${clientMessageId}`,
          }
        );

        if (result === "dropped") {
          throw new Error("The chat connection is unavailable");
        }
      } catch (error) {
        console.error("Failed to send voice note", error);

        setMessages((current) =>
          current.map((message) =>
            message.clientMessageId === clientMessageId
              ? {
                  ...message,
                  _optimistic: false,
                  _uploading: false,
                  _failed: true,
                }
              : message
          )
        );

        toast({
          title: "Voice note failed",
          description:
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "The voice note could not be sent.",
          duration: 3000,
        });
      }
    },
    [
      itemMissing,
      numericItemId,
      numericUserId,
      participantId,
      publish,
      replyingTo,
      roomId,
    ]
  );

  const closeAttachmentDraft = useCallback(() => {
    setAttachmentDraft((current) => {
      if (current?.objectUrl) {
        URL.revokeObjectURL(current.objectUrl);
      }
      return null;
    });
    setAttachmentSending(false);
  }, []);

  const prepareAttachmentDraft = useCallback(
    (file, type) => {
      if (!file || itemMissing || editingMessageId !== null) return;

      try {
        getChatAttachmentDescriptor(file, type);
      } catch (error) {
        toast({
          title: "Attachment not supported",
          description: error?.message || "Only photos, videos and audio are supported.",
          duration: 3000,
        });
        return;
      }

      setAttachmentMenuOpen(false);
      setAttachmentDraft((current) => {
        if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return {
          file,
          type: String(type).toUpperCase(),
          objectUrl: URL.createObjectURL(file),
        };
      });
    },
    [editingMessageId, itemMissing]
  );

  const uploadChatAttachment = useCallback(
    async (draft, crop = null) => {
      const file = draft?.file;
      const requestedType = draft?.type;
      if (!file || !requestedType || itemMissing || editingMessageId !== null) return;

      let descriptor;
      try {
        descriptor = getChatAttachmentDescriptor(file, requestedType);
      } catch (error) {
        toast({
          title: "Attachment not supported",
          description: error?.message || "Only photos, videos and audio are supported.",
          duration: 3000,
        });
        return;
      }

      setAttachmentSending(true);

      const clientMessageId =
        globalThis.crypto?.randomUUID?.() ||
        `${numericUserId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const replySnapshot = replyingTo;
      const localUrl = draft.objectUrl;

      let audioDurationMs = null;
      let audioMimeType = null;

      if (descriptor.type === "AUDIO") {
        audioDurationMs = await getAudioDurationMs(file);
        audioMimeType = normalizeAudioMimeType(file);
      }

      const optimisticMessage = {
        clientMessageId,
        senderId: numericUserId,
        receiverId: participantId,
        itemId: numericItemId,
        roomId,
        timestamp: Date.now(),
        message: descriptor.preview,
        messageType: descriptor.type,
        ...(descriptor.type === "AUDIO"
          ? {
              audioUrl: localUrl,
              audioDurationMs,
              audioMimeType,
              attachmentName: file.name,
              attachmentSizeBytes: file.size,
            }
          : {
              attachmentUrl: localUrl,
              attachmentName: file.name,
              attachmentMimeType: file.type || null,
              attachmentSizeBytes: file.size,
            }),
        replyToMessageId: replySnapshot?.messageId ?? null,
        replyToSenderId: replySnapshot?.senderId ?? null,
        replyToMessage: replySnapshot?.message ?? null,
        _optimistic: true,
        _uploading: true,
        _uploadProgress: 0,
      };

      setMessages((current) => mergeMessages(current, [optimisticMessage]));
      setReplyingTo(null);

      // The preview closes as soon as Send is pressed. Upload continues in the
      // message bubble and reports progress there.
      setAttachmentDraft(null);
      setAttachmentSending(false);

      try {
        const uploaded = await uploadDirectToCloudinary(
          file,
          descriptor.purpose,
          {
            onProgress: (progress) => {
              setMessages((current) =>
                current.map((message) =>
                  message.clientMessageId === clientMessageId
                    ? { ...message, _uploadProgress: progress }
                    : message
                )
              );
            },
          }
        );

        const deliveredUrl =
          descriptor.type === "IMAGE" || descriptor.type === "VIDEO"
            ? buildCroppedCloudinaryUrl(uploaded.secureUrl, crop)
            : descriptor.type === "AUDIO"
            ? buildCloudinaryAudioPlaybackUrl(uploaded.secureUrl)
            : uploaded.secureUrl;

        setMessages((current) =>
          current.map((message) =>
            message.clientMessageId === clientMessageId
              ? {
                  ...message,
                  ...(descriptor.type === "AUDIO"
                    ? {
                        audioUrl: deliveredUrl,
                        attachmentPublicId: uploaded.publicId,
                      }
                    : {
                        attachmentUrl: deliveredUrl,
                        attachmentPublicId: uploaded.publicId,
                      }),
                  attachmentSizeBytes: uploaded.bytes || file.size,
                  _uploading: false,
                  _uploadProgress: 100,
                }
              : message
          )
        );

        if (localUrl) URL.revokeObjectURL(localUrl);

        const payload = {
          clientMessageId,
          receiverId: participantId,
          itemId: numericItemId,
          roomId,
          message: descriptor.preview,
          messageType: descriptor.type,
          replyToMessageId: replySnapshot?.messageId ?? null,
          attachmentPublicId: uploaded.publicId,
          attachmentName: file.name,
          attachmentSizeBytes: uploaded.bytes || file.size,
          ...(descriptor.type === "AUDIO"
            ? {
                audioUrl: deliveredUrl,
                audioDurationMs,
                audioMimeType,
              }
            : {
                attachmentUrl: deliveredUrl,
                attachmentMimeType: file.type || null,
              }),
        };

        const result = publish(
          "/app/chat.send",
          payload,
          {
            queueIfDisconnected: true,
            queueKey: `chat-send:${clientMessageId}`,
          }
        );

        if (result === "dropped") {
          throw new Error("The chat connection is unavailable");
        }
      } catch (error) {
        console.error("Failed to send attachment", error);

        setMessages((current) =>
          current.map((message) =>
            message.clientMessageId === clientMessageId
              ? {
                  ...message,
                  _optimistic: false,
                  _uploading: false,
                  _failed: true,
                }
              : message
          )
        );

        toast({
          title: "Attachment failed",
          description:
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "The attachment could not be sent.",
          duration: 3000,
        });
      }
    },
    [
      editingMessageId,
      itemMissing,
      numericItemId,
      numericUserId,
      participantId,
      publish,
      replyingTo,
      roomId,
    ]
  );

  const handleAttachmentSelected = useCallback(
    (event, type) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) prepareAttachmentDraft(file, type);
    },
    [prepareAttachmentDraft]
  );

  const startVoiceRecording = useCallback(async () => {
    if (
      itemMissing ||
      editingMessageId !== null ||
      text.trim()
    ) {
      return;
    }

    if (
      typeof MediaRecorder === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      toast({
        title: "Voice notes are not supported",
        description: "This browser cannot access the microphone for recording.",
        duration: 3000,
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const preferredMimeType = getPreferredRecorderMimeType();
      const options = {
        audioBitsPerSecond: 64000,
        ...(preferredMimeType ? { mimeType: preferredMimeType } : {}),
      };

      const recorder = new MediaRecorder(stream, options);
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      recordingActionRef.current = "cancel";
      recordingStartedAtRef.current = Date.now();
      setRecordingElapsedMs(0);
      setIsRecording(true);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (typingSentRef.current) {
        publishTyping(false);
        typingSentRef.current = false;
      }

      recorder.ondataavailable = (event) => {
        if (event.data?.size) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        console.error("Voice recording error", event?.error || event);
        recordingActionRef.current = "cancel";
      };

      recorder.onstop = async () => {
        const action = recordingActionRef.current;
        const elapsed = Math.min(
          MAX_VOICE_DURATION_MS,
          Math.max(1, Date.now() - recordingStartedAtRef.current)
        );
        const recordedMimeType = recorder.mimeType || preferredMimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: recordedMimeType });

        clearRecordingTimer();
        stopMicrophoneTracks();
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        setIsRecording(false);
        setRecordingElapsedMs(0);

        if (action === "send" && blob.size > 0) {
          void uploadVoiceNote(blob, elapsed, recordedMimeType);
        }
      };

      recorder.start(250);

      recordingTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - recordingStartedAtRef.current;
        setRecordingElapsedMs(Math.min(elapsed, MAX_VOICE_DURATION_MS));

        if (elapsed >= MAX_VOICE_DURATION_MS && recorder.state !== "inactive") {
          recordingActionRef.current = "send";
          recorder.stop();
        }
      }, 200);
    } catch (error) {
      console.error("Unable to start voice recording", error);
      clearRecordingTimer();
      stopMicrophoneTracks();
      setIsRecording(false);
      setRecordingElapsedMs(0);

      toast({
        title: "Microphone unavailable",
        description:
          error?.name === "NotAllowedError"
            ? "Allow microphone access to record a voice note."
            : "The microphone could not be started.",
        duration: 3000,
      });
    }
  }, [
    clearRecordingTimer,
    editingMessageId,
    itemMissing,
    publishTyping,
    stopMicrophoneTracks,
    text,
    uploadVoiceNote,
  ]);

  const finishVoiceRecording = useCallback((send) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;

    recordingActionRef.current = send ? "send" : "cancel";
    recorder.stop();
  }, []);

  useEffect(() => {
    return () => {
      clearRecordingTimer();
      recordingActionRef.current = "cancel";

      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        try {
          recorder.stop();
        } catch {
          // Recorder is already shutting down.
        }
      }

      mediaRecorderRef.current = null;
      stopMicrophoneTracks();
    };
  }, [clearRecordingTimer, roomId, stopMicrophoneTracks]);

  // ================= SEND =================
  const sendMessage = useCallback(() => {
    const cleanText = text.trim();

    if (itemMissing || !cleanText) return;

    const clientMessageId =
      globalThis.crypto?.randomUUID?.() ||
      `${numericUserId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const replySnapshot = replyingTo;

    const optimisticMessage = {
      clientMessageId,
      senderId: numericUserId,
      receiverId: participantId,
      itemId: numericItemId,
      message: cleanText,
      roomId,
      timestamp: Date.now(),
      replyToMessageId: replySnapshot?.messageId ?? null,
      replyToSenderId: replySnapshot?.senderId ?? null,
      replyToMessage: replySnapshot?.message ?? null,
      _optimistic: true,
    };

    // Render immediately, even while the shared socket is reconnecting.
    // If disconnected, ChatSocketProvider queues the publish and flushes it
    // automatically as soon as the one app-wide connection is back.
    setMessages((current) => mergeMessages(current, [optimisticMessage]));
    setText("");
    setReplyingTo(null);
    sessionStorage.removeItem(draftKey);

    const result = publish(
      "/app/chat.send",
      {
        clientMessageId,
        senderId: numericUserId,
        receiverId: participantId,
        itemId: numericItemId,
        message: cleanText,
        roomId,
        replyToMessageId: replySnapshot?.messageId ?? null,
      },
      {
        queueIfDisconnected: true,
        queueKey: `chat-send:${clientMessageId}`,
      }
    );

    if (result === "dropped") {
      setMessages((current) =>
        current.map((message) =>
          message.clientMessageId === clientMessageId
            ? { ...message, _optimistic: false, _failed: true }
            : message
        )
      );

      toast({
        title: "Message not sent",
        description: "The chat connection is unavailable. Please try again.",
        duration: 2500,
      });
      return;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (typingSentRef.current) {
      publishTyping(false);
      typingSentRef.current = false;
    }
  }, [
    draftKey,
    itemMissing,
    numericItemId,
    numericUserId,
    participantId,
    publish,
    publishTyping,
    replyingTo,
    roomId,
    text,
  ]);

  const startEditingMessage = useCallback((message) => {
    const messageId = getServerMessageId(message);

    if (
      itemMissing ||
      !messageId ||
      Number(message?.senderId) !== numericUserId ||
      !isTextMessage(message) ||
      message?._optimistic ||
      message?._failed
    ) {
      return;
    }

    // Editing uses the existing bottom composer.
    setReplyingTo(null);
    setEditingMessageId(messageId);
    setEditText(String(message?.message || ""));
    setEditError("");
  }, [itemMissing, numericUserId]);

  const cancelEditingMessage = useCallback(() => {
    if (editSaving) return;
    setEditingMessageId(null);
    setEditText("");
    setEditError("");
  }, [editSaving]);

  const saveEditedMessage = useCallback(async () => {
    const cleanText = editText.trim();
    const messageId = editingMessageId;

    if (itemMissing || !messageId || !cleanText || editSaving) return;

    const existing = messages.find(
      (message) =>
        String(getServerMessageId(message)) === String(messageId)
    );

    if (existing && cleanText === String(existing.message || "").trim()) {
      cancelEditingMessage();
      return;
    }

    const previousMessage = existing ? { ...existing } : null;
    const optimisticEditedAt = Date.now();

    // WhatsApp-style optimistic edit: update the bubble and close the editor
    // immediately, then persist the edit in the background.
    setMessages((current) =>
      current.map((message) =>
        String(getServerMessageId(message)) === String(messageId)
          ? {
              ...message,
              message: cleanText,
              editedAt: optimisticEditedAt,
              _editOptimistic: true,
            }
          : message
      )
    );

    setEditingMessageId(null);
    setEditText("");
    setEditError("");

    requestAnimationFrame(() => {
      if (messageInputRef.current) {
        messageInputRef.current.style.height = "auto";
        messageInputRef.current.focus();
      }
    });

    try {
      const res = await api.put(
        `/api/chat/messages/${encodeURIComponent(messageId)}`,
        {
          roomId,
          message: cleanText,
        }
      );

      if (res.data) {
        setMessages((current) =>
          mergeMessages(current, [
            {
              ...res.data,
              _editOptimistic: false,
            },
          ])
        );
      } else {
        setMessages((current) =>
          current.map((message) =>
            String(getServerMessageId(message)) === String(messageId)
              ? {
                  ...message,
                  _editOptimistic: false,
                }
              : message
          )
        );
      }
    } catch (error) {
      console.error("Failed to edit chat message", error);

      if (previousMessage) {
        setMessages((current) =>
          current.map((message) =>
            String(getServerMessageId(message)) === String(messageId)
              ? previousMessage
              : message
          )
        );
      }

      toast({
        title: "Message edit failed",
        description:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "The original message has been restored.",
        duration: 3000,
      });
    }
  }, [
    cancelEditingMessage,
    editSaving,
    editText,
    itemMissing,
    editingMessageId,
    messages,
    roomId,
  ]);

  const participantName = participant?.username || "Marketplace user";
  const participantImage = participant?.profileImageUrl;

  const connectionLabel =
    connectionState === "connected"
      ? "Chat connected"
      : connectionState === "reconnecting"
      ? "Reconnecting..."
      : connectionState === "error"
      ? "Connection problem"
      : connectionState === "disconnected"
      ? "Disconnected"
      : "Connecting...";

  const participantStatus = participantTyping
    ? "typing..."
    : participantPresence?.online
    ? "Online"
    : participantPresence?.lastSeen
    ? `Last seen ${new Date(participantPresence.lastSeen).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : connectionLabel;

  const getDeliveryLabel = (message) => {
    if (Number(message?.senderId) !== numericUserId) return "";
    if (message?._failed) return "Failed";
    if (message?._optimistic && !message?.messageId && !message?.id) {
      return "Sending...";
    }

    const messageTime = getTimestamp(message);
    const readAt = getTimestamp({
      timestamp: message?.readAt ?? readReceipt?.readAt,
    });

    if (message?.readAt || (readReceipt && readAt >= messageTime && messageTime > 0)) {
      return "Seen";
    }

    const deliveredAt = getTimestamp({
      timestamp: message?.deliveredAt ?? deliveryReceipt?.deliveredAt,
    });
    const deliveredMessageId =
      deliveryReceipt?.messageId ?? deliveryReceipt?.lastMessageId ?? null;
    const messageId = message?.messageId ?? message?.id ?? null;

    if (
      message?.deliveredAt ||
      (deliveryReceipt &&
        ((messageId !== null && String(deliveredMessageId) === String(messageId)) ||
          (deliveredAt >= messageTime && messageTime > 0)))
    ) {
      return "Delivered";
    }

    return "Sent";
  };

  return (
  <div className="w-full h-full flex flex-col bg-background">
    <style>{`
      .chat-tiny-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .chat-tiny-scrollbar::-webkit-scrollbar {
        width: 2px;
        height: 2px;
      }

      .chat-tiny-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }

      .chat-tiny-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.16);
        border-radius: 999px;
      }

      .chat-tiny-scrollbar:hover::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.28);
      }
    `}</style>
    <div className="w-full flex-1 flex flex-col bg-card overflow-hidden">

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div className="shrink-0 border-b border-border bg-card px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
        {/* Mobile navigation belongs inside the chat header */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to inbox"
            className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted active:bg-muted/80"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.2} />
          </button>
        )}

        {participantImage ? (
          <ProfileImageViewer
            src={participantImage}
            alt={`${participantName} profile`}
            title={participantName}
            triggerClassName="h-10 w-10 shrink-0 rounded-full"
            imageClassName="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <div className="h-10 w-10 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
            {participantName.charAt(0).toUpperCase()}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="truncate text-sm font-semibold">
              {participantName}
            </h2>

            <span
              className={`shrink-0 text-[10px] ${
                connected
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
            >
              {participantStatus}
            </span>
          </div>

          <p className="truncate text-xs text-muted-foreground">
            Regarding: {itemName || `Item #${itemId}`}
          </p>
        </div>
      </div>

      {/* ===================================================== */}
      {/* ITEM NO LONGER EXISTS */}
      {/* ===================================================== */}

      {itemMissing && (
        <div className="shrink-0 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs text-amber-700 dark:text-amber-300">
          <span className="font-semibold">
            Item no longer exists.
          </span>{" "}
          This conversation is read-only; previous messages remain available.
        </div>
      )}

      {/* ===================================================== */}
      {/* MESSAGES */}
      {/* ===================================================== */}

      <div
        className="
          chat-tiny-scrollbar
          flex-1
          overflow-y-auto
          overflow-x-hidden
          bg-background/40
          px-2.5
          py-3
          sm:px-4
        "
      >
        {/* =================================================== */}
        {/* LOAD EARLIER */}
        {/* =================================================== */}

        {historyHasMore && (
          <div className="mb-4 flex justify-center">
            <button
              type="button"
              onClick={loadEarlierMessages}
              disabled={historyLoadingEarlier}
              className="
                rounded-full
                border
                border-border
                bg-card/90
                px-3
                py-1
                text-[10px]
                font-medium
                text-primary
                shadow-sm
                backdrop-blur
                disabled:opacity-50
              "
            >
              {historyLoadingEarlier
                ? "Loading..."
                : "Load earlier messages"}
            </button>
          </div>
        )}

        {/* =================================================== */}
        {/* LOADING */}
        {/* =================================================== */}

        {historyLoading && messages.length === 0 && (
          <p className="py-5 text-center text-xs text-muted-foreground">
            Loading conversation...
          </p>
        )}

        {/* =================================================== */}
        {/* HISTORY ERROR */}
        {/* =================================================== */}

        {historyError && (
          <p className="py-3 text-center text-xs text-destructive">
            {historyError}
          </p>
        )}

        {/* =================================================== */}
        {/* EMPTY CONVERSATION */}
        {/* =================================================== */}

        {!historyLoading &&
          !historyError &&
          messages.length === 0 && (
            <p className="py-5 text-center text-xs text-muted-foreground">
              No messages yet. Start the conversation.
            </p>
          )}

        {/* =================================================== */}
        {/* MESSAGE LIST */}
        {/* =================================================== */}

        <div className="space-y-[3px]">
          {messages.map((msg, index) => {
            const isMe =
              Number(msg.senderId) === numericUserId;

            const serverMessageId =
              getServerMessageId(msg);

            const time =
              formatMessageTime(msg);

            const voiceMessage =
              isVoiceMessage(msg);

            const mediaAttachmentMessage =
              isMediaAttachmentMessage(msg);

            /* =============================================== */
            /* MESSAGE TIMESTAMP */
            /* =============================================== */

            const rawTimestamp =
              msg?.timestamp ??
              msg?.createdAt ??
              msg?.sentAt;

            const previousMessage =
              index > 0
                ? messages[index - 1]
                : null;

            const previousTimestamp =
              previousMessage?.timestamp ??
              previousMessage?.createdAt ??
              previousMessage?.sentAt;

            const parseMessageDate = (value) => {
              if (!value) {
                return null;
              }

              let normalizedValue = value;

              // Epoch seconds -> milliseconds
              if (
                typeof normalizedValue === "number" &&
                normalizedValue < 10000000000
              ) {
                normalizedValue *= 1000;
              }

              const parsed =
                new Date(normalizedValue);

              return Number.isNaN(parsed.getTime())
                ? null
                : parsed;
            };

            const messageDate =
              parseMessageDate(rawTimestamp);

            const previousDate =
              parseMessageDate(previousTimestamp);

            /* =============================================== */
            /* DAY KEY */
            /* =============================================== */

            const getDayKey = (date) => {
              if (!date) {
                return null;
              }

              return [
                date.getFullYear(),
                String(
                  date.getMonth() + 1
                ).padStart(2, "0"),
                String(
                  date.getDate()
                ).padStart(2, "0"),
              ].join("-");
            };

            const currentDayKey =
              getDayKey(messageDate);

            const previousDayKey =
              getDayKey(previousDate);

            const showDaySeparator =
              currentDayKey &&
              currentDayKey !== previousDayKey;

            // WhatsApp-style grouping: only the first bubble in a consecutive
            // run from the same sender gets a tail. A new day always starts
            // a new visual group.
            const previousIsSameSender =
              previousMessage &&
              Number(previousMessage?.senderId) === Number(msg?.senderId) &&
              currentDayKey &&
              currentDayKey === previousDayKey;

            const startsMessageGroup = !previousIsSameSender;

            /* =============================================== */
            /* DAY LABEL */
            /* =============================================== */

            let dayLabel = "";

            if (messageDate) {
              const today =
                new Date();

              today.setHours(
                0,
                0,
                0,
                0
              );

              const messageDay =
                new Date(messageDate);

              messageDay.setHours(
                0,
                0,
                0,
                0
              );

              const yesterday =
                new Date(today);

              yesterday.setDate(
                yesterday.getDate() - 1
              );

              const differenceInDays =
                Math.round(
                  (
                    today.getTime() -
                    messageDay.getTime()
                  ) /
                    86400000
                );

              if (
                messageDay.getTime() ===
                today.getTime()
              ) {
                dayLabel = "Today";
              } else if (
                messageDay.getTime() ===
                yesterday.getTime()
              ) {
                dayLabel = "Yesterday";
              } else if (
                differenceInDays > 1 &&
                differenceInDays < 7
              ) {
                dayLabel =
                  messageDate.toLocaleDateString(
                    undefined,
                    {
                      weekday: "long",
                    }
                  );
              } else {
                dayLabel =
                  messageDate.toLocaleDateString(
                    undefined,
                    {
                      day: "numeric",
                      month: "long",

                      year:
                        messageDate.getFullYear() !==
                        today.getFullYear()
                          ? "numeric"
                          : undefined,
                    }
                  );
              }
            }

            /* =============================================== */
            /* MESSAGE STATUS */
            /* =============================================== */

            const renderMessageStatus = () => {
              if (!isMe) return null;

              // Edited messages are shown immediately. While the background PUT
              // is still pending, show a clock in the bubble.
              if (msg?._editOptimistic) {
                return (
                  <Clock3
                    className="h-[12px] w-[12px] text-white/55"
                    strokeWidth={2}
                  />
                );
              }

              // Use the existing receipt-aware helper so live STOMP read/delivery
              // receipts are reflected even before the message object is reloaded.
              const status = getDeliveryLabel(msg);

              if (status === "Sending..." || status === "Failed") {
                return (
                  <Clock3
                    className={`h-[12px] w-[12px] ${
                      status === "Failed" ? "text-red-300" : "text-white/55"
                    }`}
                    strokeWidth={2}
                  />
                );
              }

              if (status === "Seen") {
                return (
                  <WhatsAppDoubleTick className="h-[13px] w-[19px] text-[#53BDEB]" />
                );
              }

              if (status === "Delivered") {
                return (
                  <WhatsAppDoubleTick className="h-[13px] w-[19px] text-white/60" />
                );
              }

              return (
                <WhatsAppSingleTick className="h-[13px] w-[17px] text-white/60" />
              );
            };

            return (
              <div
                key={getMessageIdentity(msg)}
                className="w-full"
              >
                {/* =========================================== */}
                {/* DATE/DAY DIVIDER */}
                {/* =========================================== */}

                {showDaySeparator && (
                  <div className="flex justify-center py-3">
                    <span
                      className="
                        rounded-md
                        border
                        border-border/40
                        bg-card/85
                        px-3
                        py-1
                        text-[10px]
                        font-medium
                        text-muted-foreground
                        shadow-sm
                        backdrop-blur-md
                      "
                    >
                      {dayLabel}
                    </span>
                  </div>
                )}

                {/* =========================================== */}
                {/* MESSAGE ROW */}
                {/* =========================================== */}

                <div
                  className={`flex w-full ${
                    isMe
                      ? "justify-end"
                      : "justify-start"
                  } ${startsMessageGroup ? "mt-2" : "mt-[2px]"}`}
                >
                  <SwipeableMessage
                    replyDisabled={
                      itemMissing ||
                      !serverMessageId ||
                      msg?._optimistic ||
                      msg?._failed ||
                      String(
                        editingMessageId
                      ) ===
                        String(
                          serverMessageId
                        )
                    }
                    actionsDisabled={
                      String(
                        editingMessageId
                      ) ===
                      String(
                        serverMessageId
                      )
                    }
                    onReply={() =>
                      startReplyingToMessage(
                        msg
                      )
                    }
                    onLongPress={() =>
                      openMessageActions(
                        msg
                      )
                    }
                  >
                    <div
                      ref={(node) => {
                        if (!serverMessageId) {
                          return;
                        }

                        const key =
                          String(
                            serverMessageId
                          );

                        if (node) {
                          messageNodeRefs.current.set(
                            key,
                            node
                          );
                        } else {
                          messageNodeRefs.current.delete(
                            key
                          );
                        }
                      }}
                      className={`
                        min-w-0
                        w-full
                        max-w-full
                        transition-all
                        duration-150

                        ${
                          highlightedMessageId ===
                            String(
                              serverMessageId
                            ) ||
                          (
                            messageActions &&
                            serverMessageId !==
                              null &&
                            String(
                              messageActions.messageId
                            ) ===
                              String(
                                serverMessageId
                              )
                          )
                            ? `
                              rounded-lg
                              ring-2
                              ring-primary/70
                              ring-offset-1
                              ring-offset-background
                            `
                            : ""
                        }
                      `}
                    >
                      {/* ===================================== */}
                      {/* MESSAGE BUBBLE */}
                      {/* ===================================== */}

                      <div
                        className={`
                          relative
                          min-w-[76px]
                          w-fit
                          max-w-full
                          overflow-visible
                          border
                          px-[10px]
                          pt-[6px]
                          pb-[5px]
                          text-[14px]
                          leading-[19px]
                          shadow-sm

                          ${
                            isMe
                              ? `
                                rounded-[8px]
                                ${startsMessageGroup ? "rounded-tr-[2px]" : "rounded-tr-[8px]"}
                                border-[#0B6B5D]
                                bg-[#005C4B]
                                text-white
                                ${
                                  startsMessageGroup
                                    ? `
                                      before:pointer-events-none
                                      before:absolute
                                      before:right-[-9px]
                                      before:top-[-0.6px]
                                      before:h-[14px]
                                      before:w-[10px]
                                      before:bg-[#0B6B5D]
                                      before:[clip-path:polygon(0_0,100%_0,0_100%)]
                                      after:pointer-events-none
                                      after:absolute
                                      after:right-[-7px]
                                      after:top-0
                                      after:h-[12px]
                                      after:w-[8px]
                                      after:bg-[#005C4B]
                                      after:[clip-path:polygon(0_0,100%_0,0_100%)]
                                    `
                                    : ""
                                }
                              `
                              : `
                                rounded-[8px]
                                ${startsMessageGroup ? "rounded-tl-[2px]" : "rounded-tl-[8px]"}
                                border-border
                                bg-card
                                text-foreground
                                ${
                                  startsMessageGroup
                                    ? `
                                      before:pointer-events-none
                                      before:absolute
                                      before:left-[-9.9px]
                                      before:top-[-0.6px]
                                      before:h-[14px]
                                      before:w-[10px]
                                      before:bg-border
                                      before:[clip-path:polygon(100%_0,0_0,100%_100%)]
                                      after:pointer-events-none
                                      after:absolute
                                      after:left-[-8px]
                                      after:top-0
                                      after:h-[12px]
                                      after:w-[8px]
                                      after:bg-card
                                      after:[clip-path:polygon(100%_0,0_0,100%_100%)]
                                    `
                                    : ""
                                }
                              `
                          }
                        `}
                      >
                        {/* =================================== */}
                        {/* REPLIED MESSAGE PREVIEW */}
                        {/* =================================== */}

                        {msg?.replyToMessageId && (
                          <button
                            type="button"
                            data-no-swipe="true"
                            onClick={() =>
                              scrollToRepliedMessage(
                                msg.replyToMessageId
                              )
                            }
                            className={`
                              mb-1.5
                              block
                              w-full
                              overflow-hidden
                              rounded-md
                              border-l-[3px]
                              px-2
                              py-1.5
                              text-left

                              ${
                                isMe
                                  ? `
                                    border-[#53BDEB]
                                    bg-black/15
                                  `
                                  : `
                                    border-primary
                                    bg-secondary
                                  `
                              }
                            `}
                          >
                            <span
                              className={`
                                block
                                truncate
                                text-[10px]
                                font-semibold

                                ${
                                  isMe
                                    ? "text-[#8ed8f8]"
                                    : "text-primary"
                                }
                              `}
                            >
                              {Number(
                                msg.replyToSenderId
                              ) ===
                              numericUserId
                                ? "You"
                                : participantName}
                            </span>

                            <span
                              className={`
                                mt-[1px]
                                block
                                line-clamp-2
                                text-[11px]
                                leading-[15px]

                                ${
                                  isMe
                                    ? "text-white/70"
                                    : "text-muted-foreground"
                                }
                              `}
                            >
                              {msg.replyToMessage ||
                                "Original message"}
                            </span>
                          </button>
                        )}

                        {/* =================================== */}
                        {/* MESSAGE CONTENT + INLINE METADATA */}

                        {voiceMessage ? (
                          <div className="min-w-0">
                            {msg?.attachmentName && (
                              <div className={`mb-1 flex items-center gap-1.5 text-[11px] font-medium ${
                                isMe ? "text-white/85" : "text-foreground"
                              }`}>
                                <Music2 className="h-3.5 w-3.5" />
                                <span className="max-w-[220px] truncate">{msg.attachmentName}</span>
                              </div>
                            )}
                            <VoiceNotePlayer
                              src={msg.audioUrl}
                              durationMs={msg.audioDurationMs || 0}
                              isMe={isMe}
                            />

                            {msg?._uploading && (
                              <div className={`mt-1 text-[9px] ${isMe ? "text-white/60" : "text-muted-foreground"}`}>
                                Uploading {Number(msg?._uploadProgress || 0)}%
                              </div>
                            )}

                            <div
                              className={`mt-0.5 flex items-center justify-end gap-[2px] whitespace-nowrap text-[9.5px] leading-none ${
                                isMe ? "text-white/55" : "text-muted-foreground"
                              }`}
                            >
                              {time && <span>{time}</span>}

                              {isMe && (
                                <span
                                  className="ml-[1px] inline-flex items-center"
                                  title={getDeliveryLabel(msg)}
                                >
                                  {renderMessageStatus()}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : mediaAttachmentMessage ? (
                          <div className="min-w-0">
                            <AttachmentContent
                              message={msg}
                              onOpen={setMediaViewerMessage}
                            />

                            <div
                              className={`mt-1 flex items-center justify-end gap-[2px] whitespace-nowrap text-[9.5px] leading-none ${
                                isMe ? "text-white/55" : "text-muted-foreground"
                              }`}
                            >
                              {time && <span>{time}</span>}

                              {isMe && (
                                <span
                                  className="ml-[1px] inline-flex items-center"
                                  title={getDeliveryLabel(msg)}
                                >
                                  {renderMessageStatus()}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="min-w-0 max-w-full whitespace-normal break-words [overflow-wrap:anywhere]">
                            <span className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                              {msg.message}
                            </span>

                            <span
                              className={`ml-1.5 inline-flex translate-y-[2px] items-center gap-[2px] whitespace-nowrap align-baseline text-[9.5px] leading-none ${
                                isMe ? "text-white/55" : "text-muted-foreground"
                              }`}
                            >
                              {msg?.editedAt && (
                                <span className="mr-[1px]">edited</span>
                              )}

                              {time && <span>{time}</span>}

                              {isMe && (
                                <span
                                  className="ml-[1px] inline-flex items-center"
                                  title={getDeliveryLabel(msg)}
                                >
                                  {renderMessageStatus()}
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </SwipeableMessage>
                </div>
              </div>
            );
          })}
        </div>

        {/* =================================================== */}
        {/* TYPING */}
        {/* =================================================== */}

        {participantTyping && (
          <div className="mt-1.5 flex justify-start">
            <div
              className="
                rounded-lg
                rounded-tl-[3px]
                border
                border-border
                bg-card
                px-3
                py-2
                text-[11px]
                text-muted-foreground
                shadow-sm
              "
            >
              {participantName} is typing...
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <MediaViewer
        media={mediaViewerMessage}
        onClose={() => setMediaViewerMessage(null)}
      />

      {attachmentDraft?.type === "AUDIO" ? (
        <AudioSendPreview
          draft={attachmentDraft}
          sending={attachmentSending}
          onCancel={closeAttachmentDraft}
          onSend={() => uploadChatAttachment(attachmentDraft)}
        />
      ) : (
        <MediaSendPreview
          draft={attachmentDraft}
          sending={attachmentSending}
          onCancel={closeAttachmentDraft}
          onSend={(crop) => uploadChatAttachment(attachmentDraft, crop)}
        />
      )}

      {/* ===================================================== */}
      {/* MESSAGE ACTIONS */}
      {/* ===================================================== */}

      {messageActions && (
        <div
          className="
            fixed
            inset-0
            z-[70]
            bg-black/10
            backdrop-blur-[1px]
          "
          onPointerDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeMessageActions();
            }
          }}
        >
          <div
            className="
              absolute
              bottom-20
              left-1/2
              w-[min(92vw,360px)]
              -translate-x-1/2
              rounded-2xl
              border
              border-border
              bg-card
              p-2
              shadow-2xl
            "
          >
            {/* MESSAGE PREVIEW */}

            <div className="mb-1 flex items-center justify-between px-2 py-1">
              <p className="max-w-[280px] truncate text-xs text-muted-foreground">
                {getMessagePreview(messageActions.message) || "Message"}
              </p>

              <button
                type="button"
                onClick={
                  closeMessageActions
                }
                aria-label="Close message actions"
                className="
                  rounded-full
                  p-1
                  text-muted-foreground
                  hover:bg-muted
                  hover:text-foreground
                "
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-1">

              {/* ============================================= */}
              {/* REPLY */}
              {/* ============================================= */}

              {!itemMissing &&
                getServerMessageId(
                  messageActions.message
                ) &&
                !messageActions.message
                  ?._optimistic &&
                !messageActions.message
                  ?._failed && (
                  <button
                    type="button"
                    onClick={() => {
                      const message =
                        messageActions.message;

                      closeMessageActions();

                      startReplyingToMessage(
                        message
                      );
                    }}
                    className="
                      flex
                      flex-col
                      items-center
                      justify-center
                      gap-1
                      rounded-xl
                      px-3
                      py-3
                      text-xs
                      font-medium
                      hover:bg-muted
                    "
                  >
                    <Reply className="h-5 w-5 text-primary" />
                    Reply
                  </button>
                )}

              {/* ============================================= */}
              {/* COPY */}
              {/* ============================================= */}

              {isTextMessage(messageActions.message) && (
              <button
                type="button"
                onClick={() =>
                  copyMessageText(
                    messageActions.message
                  )
                }
                className="
                  flex
                  flex-col
                  items-center
                  justify-center
                  gap-1
                  rounded-xl
                  px-3
                  py-3
                  text-xs
                  font-medium
                  hover:bg-muted
                "
              >
                <Copy className="h-5 w-5 text-primary" />
                Copy
              </button>
              )}

              {/* ============================================= */}
              {/* EDIT */}
              {/* ============================================= */}

              {!itemMissing &&
                isTextMessage(messageActions.message) &&
                Number(
                  messageActions.message
                    ?.senderId
                ) === numericUserId &&
                getServerMessageId(
                  messageActions.message
                ) &&
                !messageActions.message
                  ?._optimistic &&
                !messageActions.message
                  ?._failed && (
                  <button
                    type="button"
                    onClick={() => {
                      const message =
                        messageActions.message;

                      closeMessageActions();

                      startEditingMessage(
                        message
                      );
                    }}
                    className="
                      flex
                      flex-col
                      items-center
                      justify-center
                      gap-1
                      rounded-xl
                      px-3
                      py-3
                      text-xs
                      font-medium
                      hover:bg-muted
                    "
                  >
                    <Pencil className="h-5 w-5 text-primary" />
                    Edit
                  </button>
                )}
            </div>
          </div>
        </div>
      )}

      {/* ===================================================== */}
      {/* COMPOSER */}
      {/* ===================================================== */}

      <div className="shrink-0 border-t border-border bg-card">

        {/* =================================================== */}
        {/* EDIT MODE BANNER */}
        {/* Uses SAME composer, not another textarea */}
        {/* =================================================== */}

        {editingMessageId !== null &&
          !itemMissing && (
            <div className="px-3 pt-2">
              <div
                className="
                  flex
                  items-stretch
                  overflow-hidden
                  rounded-lg
                  border
                  border-border
                  bg-secondary/70
                "
              >
                <div className="w-[3px] shrink-0 bg-primary" />

                <div className="min-w-0 flex-1 px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Pencil className="h-3.5 w-3.5 text-primary" />

                    <span className="text-xs font-semibold text-primary">
                      Edit message
                    </span>
                  </div>

                  <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                    Edit your message below
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    cancelEditingMessage();

                    requestAnimationFrame(
                      () => {
                        if (
                          messageInputRef.current
                        ) {
                          messageInputRef.current.style.height =
                            "auto";
                        }
                      }
                    );
                  }}
                  aria-label="Cancel editing"
                  className="
                    flex
                    w-10
                    shrink-0
                    items-center
                    justify-center
                    text-muted-foreground
                    hover:bg-muted
                    hover:text-foreground
                  "
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

        {/* =================================================== */}
        {/* REPLY PREVIEW */}
        {/* Hide when editing */}
        {/* =================================================== */}

        {editingMessageId === null &&
          replyingTo &&
          !itemMissing && (
            <div className="px-3 pt-2">
              <div
                className="
                  flex
                  items-stretch
                  overflow-hidden
                  rounded-lg
                  border
                  border-border
                  bg-secondary/70
                "
              >
                <div className="w-[3px] shrink-0 bg-primary" />

                <div className="min-w-0 flex-1 px-3 py-2">
                  <div className="flex items-center gap-1 text-xs font-semibold text-primary">
                    <Reply className="h-3.5 w-3.5" />

                    <span>
                      {Number(
                        replyingTo.senderId
                      ) === numericUserId
                        ? "You"
                        : participantName}
                    </span>
                  </div>

                  <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                    {replyingTo.message}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={cancelReply}
                  aria-label="Cancel reply"
                  className="
                    flex
                    w-10
                    shrink-0
                    items-center
                    justify-center
                    text-muted-foreground
                    hover:bg-muted
                    hover:text-foreground
                  "
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

        {/* =================================================== */}
        {/* EDIT ERROR */}
        {/* =================================================== */}

        {editingMessageId !== null &&
          editError && (
            <p className="px-4 pt-1.5 text-[10px] text-destructive">
              {editError}
            </p>
          )}

        {/* =================================================== */}
        {/* ACTUAL MESSAGE INPUT / VOICE RECORDER */}

        {isRecording ? (
          <div className="flex items-center gap-2 p-3">
            <button
              type="button"
              onClick={() => finishVoiceRecording(false)}
              aria-label="Cancel voice note"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-destructive transition hover:bg-destructive/10"
            >
              <Trash2 className="h-5 w-5" />
            </button>

            <div className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 rounded-2xl bg-secondary px-4">
              <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-500" />

              <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
                {formatVoiceDuration(recordingElapsedMs)}
              </span>

              <div className="flex min-w-0 flex-1 items-end justify-center gap-[3px] overflow-hidden">
                {[8, 14, 20, 11, 17, 23, 13, 19, 9, 16, 21, 12].map(
                  (height, index) => (
                    <span
                      key={index}
                      className="w-[3px] animate-pulse rounded-full bg-primary/60"
                      style={{
                        height: `${height}px`,
                        animationDelay: `${index * 55}ms`,
                      }}
                    />
                  )
                )}
              </div>

              <span className="shrink-0 text-[10px] text-muted-foreground">
                5:00 max
              </span>
            </div>

            <button
              type="button"
              onClick={() => finishVoiceRecording(true)}
              aria-label="Send voice note"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white transition hover:opacity-90"
            >
              <SendHorizontal className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-2 p-3">
            <input
              ref={photoInputRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(event) => handleAttachmentSelected(event, "IMAGE")}
            />
            <input
              ref={videoInputRef}
              type="file"
              className="hidden"
              accept="video/*"
              onChange={(event) => handleAttachmentSelected(event, "VIDEO")}
            />
            <input
              ref={audioInputRef}
              type="file"
              className="hidden"
              accept="audio/*,.aac,.aif,.aiff,.amr,.au,.caf,.flac,.m4a,.mid,.midi,.mp3,.mp4,.oga,.ogg,.opus,.wav,.wave,.webm,.wma,.3gp,.3gpp"
              onChange={(event) => handleAttachmentSelected(event, "AUDIO")}
            />

            {editingMessageId === null && (
              <div className="relative flex shrink-0 items-center gap-1">
                {attachmentMenuOpen && (
                  <div className="absolute bottom-14 left-0 z-40 flex items-center gap-1 rounded-2xl border border-border bg-card p-1.5 shadow-xl">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-primary hover:bg-muted"
                      aria-label="Attach photo"
                      title="Photo"
                    >
                      <ImageIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => videoInputRef.current?.click()}
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-primary hover:bg-muted"
                      aria-label="Attach video"
                      title="Video"
                    >
                      <Video className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => audioInputRef.current?.click()}
                      className="flex h-10 w-10 items-center justify-center rounded-xl text-primary hover:bg-muted"
                      aria-label="Attach audio"
                      title="Audio"
                    >
                      <Music2 className="h-5 w-5" />
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setAttachmentMenuOpen((open) => !open)}
                  disabled={itemMissing}
                  aria-label="Attach media"
                  title="Attach photo, video or audio"
                  className={`flex h-11 w-11 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    attachmentMenuOpen
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <Crop className="h-5 w-5" />
                </button>
              </div>
            )}

            <textarea
              ref={messageInputRef}
              value={editingMessageId !== null ? editText : text}
              rows={1}
              maxLength={2000}
              disabled={
                itemMissing ||
                (editingMessageId !== null && editSaving)
              }
              onChange={(event) => {
                const value = event.target.value;

                if (editingMessageId !== null) {
                  setEditText(value);
                } else {
                  handleTextChange(value);
                }

                resizeGrowingTextarea(event.currentTarget);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  if (editingMessageId !== null) {
                    event.preventDefault();
                    cancelEditingMessage();

                    requestAnimationFrame(() => {
                      if (messageInputRef.current) {
                        messageInputRef.current.style.height = "auto";
                      }
                    });
                    return;
                  }

                  if (replyingTo) {
                    event.preventDefault();
                    cancelReply();
                    return;
                  }
                }

                if (event.key === "Enter" && !event.shiftKey) {
                  const isSmallScreen = window.matchMedia(
                    "(max-width: 767px)"
                  ).matches;

                  // On mobile/small screens, Enter inserts a new line.
                  // Sending/saving is done only with the action button.
                  if (isSmallScreen) {
                    return;
                  }

                  // Desktop: Enter sends/saves.
                  // Shift + Enter keeps the default newline behaviour.
                  event.preventDefault();

                  if (editingMessageId !== null) {
                    if (editText.trim() && !editSaving) {
                      saveEditedMessage();
                    }
                  } else {
                    sendMessage();
                  }
                }
              }}
              placeholder={
                itemMissing
                  ? "Item no longer exists — conversation is read-only"
                  : editingMessageId !== null
                  ? "Edit message..."
                  : replyingTo
                  ? "Reply..."
                  : connectionState === "reconnecting"
                  ? "Type message — sends when reconnected..."
                  : "Type message..."
              }
              className="chat-tiny-scrollbar max-h-40 min-h-[42px] flex-1 resize-none overflow-y-auto rounded-2xl border border-border bg-secondary px-4 py-2.5 text-sm outline-none transition focus:border-primary/40 focus:ring-1 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
            />

            {editingMessageId !== null ? (
              <button
                type="button"
                onClick={saveEditedMessage}
                disabled={itemMissing || !editText.trim() || editSaving}
                className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editSaving ? "Saving..." : "Save"}
              </button>
            ) : text.trim() ? (
              <button
                type="button"
                onClick={sendMessage}
                disabled={itemMissing}
                className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SendHorizontal className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={startVoiceRecording}
                disabled={itemMissing}
                aria-label="Record voice note"
                title="Record voice note"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Mic className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default ChatWindow;
